import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { getPlatformOrgs, updateOrgZavvyFocusEnabled } from '../../Api/Platform';

const formatWhen = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

const lastChanged = (org) => {
  const when = formatWhen(org.zavvy_focus_updated_at);
  if (!when && !org.zavvy_focus_updated_by) return 'Not changed yet';
  if (org.zavvy_focus_updated_by && when) return `${org.zavvy_focus_updated_by}, ${when}`;
  return org.zavvy_focus_updated_by || when;
};

const TrinityZavvyChat = () => {
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [savingId, setSavingId] = useState(null);

  const fetchOrgs = useCallback(() => {
    setLoading(true);
    getPlatformOrgs({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      q: search.trim() || undefined,
    })
      .then((res) => {
        setOrgs(res?.data?.data || []);
        setTotal(res?.data?.total || 0);
      })
      .catch((err) => {
        console.error('Failed to fetch organizations:', err);
        toast.error('Failed to load organizations');
        setOrgs([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const handleToggle = async (org) => {
    const next = !org.is_zavvy_focus_enabled;
    setSavingId(org.id);
    setOrgs((prev) => prev.map((row) => (
      row.id === org.id ? { ...row, is_zavvy_focus_enabled: next } : row
    )));
    try {
      await updateOrgZavvyFocusEnabled(org.id, next);
      toast.success(`Zavvy chat ${next ? 'on' : 'off'} for ${org.organization_name}`);
      fetchOrgs();
    } catch (err) {
      console.error('Failed to update Zavvy chat:', err);
      toast.error('Failed to update Zavvy chat');
      setOrgs((prev) => prev.map((row) => (
        row.id === org.id ? { ...row, is_zavvy_focus_enabled: !next } : row
      )));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>Zavvy Chat</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Off by default. Turning it on shows the new Zavvy chat in Focus Mode for every user of that organisation. Turning it off returns them to classic Focus Mode. Chat history is kept. Turning an organisation back on runs routines that came due while it was off, as one catch-up.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          label="Search organisations"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Organisation</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Zavvy chat</TableCell>
              <TableCell>Last changed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orgs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading...' : 'No matching organisations'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : orgs.map((org) => (
              <TableRow key={org.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {org.organization_name}
                  </Typography>
                  {org.subscription_status ? (
                    <Typography variant="caption" color="text.secondary">
                      {org.subscription_status}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{org.country || ''}</TableCell>
                <TableCell>
                  <Switch
                    checked={!!org.is_zavvy_focus_enabled}
                    onChange={() => handleToggle(org)}
                    disabled={savingId === org.id}
                    inputProps={{ 'aria-label': `Zavvy chat for ${org.organization_name}` }}
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {lastChanged(org)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};

export default TrinityZavvyChat;
